import $ from "jquery";
import moment from "moment";
import "daterangepicker";

const getDashboardWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const getDashboardQuarterStart = (date) => {
  const month = date.getMonth();
  const quarter = Math.floor(month / 3);
  return new Date(date.getFullYear(), quarter * 3, 1);
};

const getDashboardFinancialYearStart = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  if (month < 3) return new Date(year - 1, 3, 1);
  return new Date(year, 3, 1);
};

export const getDashboardFinancialYearRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const fyStartYear = month < 3 ? year - 1 : year;
  const fyEndYear = month < 3 ? year : year + 1;
  const start = `${fyStartYear}-04-01`;
  const end = `${fyEndYear}-03-31`;
  const fmt = (y, m, d) => {
    const dd = String(d).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    return `${dd}-${mm}-${y}`;
  };
  const displayText = `${fmt(fyStartYear, 4, 1)} - ${fmt(fyEndYear, 3, 31)}`;
  return { dateRangeStart: start, dateRangeEnd: end, dateRange: displayText };
};

/** Each preset uses its own cloned moments (daterangepicker mutates range dates in place). */
export const getDashboardDateRangePresets = () => {
  const now = moment();
  const todayStart = now.clone().startOf("day");
  const todayEnd = now.clone().endOf("day");

  const yesterday = now.clone().subtract(1, "days");
  const yesterdayStart = yesterday.clone().startOf("day");
  const yesterdayEnd = yesterday.clone().endOf("day");

  const weekStart = moment(getDashboardWeekStart(new Date())).startOf("day");
  const weekEnd = weekStart.clone().add(6, "days").endOf("day");
  const lastWeekStart = weekStart.clone().subtract(7, "days").startOf("day");
  const lastWeekEnd = weekStart.clone().subtract(1, "days").endOf("day");

  const monthStart = now.clone().startOf("month");
  const monthEnd = now.clone().endOf("month");
  const lastMonthStart = now.clone().subtract(1, "month").startOf("month");
  const lastMonthEnd = now.clone().subtract(1, "month").endOf("month");

  const quarterStart = moment(getDashboardQuarterStart(new Date())).startOf("day");
  const quarterEnd = quarterStart.clone().add(2, "months").endOf("month");
  const lastQuarterStart = quarterStart.clone().subtract(3, "months").startOf("day");
  const lastQuarterEnd = quarterStart.clone().subtract(1, "days").endOf("day");

  const fyStart = moment(getDashboardFinancialYearStart(new Date())).startOf("day");
  const fyEnd = fyStart.clone().add(1, "year").subtract(1, "day").endOf("day");
  const lastFyStart = fyStart.clone().subtract(1, "year").startOf("day");
  const lastFyEnd = fyStart.clone().subtract(1, "days").endOf("day");

  return {
    Today: [todayStart.clone(), todayEnd.clone()],
    Yesterday: [yesterdayStart.clone(), yesterdayEnd.clone()],
    "This Week": [weekStart.clone(), weekEnd.clone()],
    "Last Week": [lastWeekStart.clone(), lastWeekEnd.clone()],
    "This Month": [monthStart.clone(), monthEnd.clone()],
    "Last Month": [lastMonthStart.clone(), lastMonthEnd.clone()],
    "This Quarter": [quarterStart.clone(), quarterEnd.clone()],
    "Last Quarter": [lastQuarterStart.clone(), lastQuarterEnd.clone()],
    "This Financial Year": [fyStart.clone(), fyEnd.clone()],
    "Last Financial Year": [lastFyStart.clone(), lastFyEnd.clone()],
  };
};

/**
 * Width in px for chart header date inputs (~0.8125rem font).
 * Numeric date ranges use a tighter per-character estimate than `ch` (which over-reserves space).
 */
export const getChartDateInputWidthPx = (displayText) => {
  const text = String(displayText || "").trim() || "Select date range";
  const isNumericDate = /^[\d\s\-]+$/.test(text);
  const charPx = isNumericDate ? 6.75 : 7.4;
  const horizontalPad = 46; // left 10px + right 36px for calendar icon
  const width = Math.ceil(text.length * charPx) + horizontalPad + 2;
  return Math.min(Math.max(width, 148), 280);
};

export const initDashboardCardDateRangePicker = (inputEl, { initialDisplay, onApply }) => {
  if (!inputEl) return () => {};
  const $input = $(inputEl);
  if ($input.data("daterangepicker")) {
    $input.off("apply.daterangepicker");
    $input.off("cancel.daterangepicker");
    $input.data("daterangepicker").remove();
  }
  $input.daterangepicker({
    autoUpdateInput: false,
    locale: {
      cancelLabel: "Clear",
      applyLabel: "Apply",
      format: "DD-MM-YYYY",
    },
    opens: "left",
    drops: "down",
    showDropdowns: true,
    ranges: getDashboardDateRangePresets(),
  });
  if (initialDisplay) $input.val(initialDisplay);
  $input.on("apply.daterangepicker", function (ev, picker) {
    const displayText =
      picker.startDate.format("DD-MM-YYYY") +
      " - " +
      picker.endDate.format("DD-MM-YYYY");
    const start = picker.startDate.format("YYYY-MM-DD");
    const end = picker.endDate.format("YYYY-MM-DD");
    $(this).val(displayText);
    onApply?.({ start, end, display: displayText });
  });
  $input.on("cancel.daterangepicker", function () {
    $(this).val("");
    onApply?.({ start: "", end: "", display: "" });
  });
  return () => {
    if ($input.data("daterangepicker")) {
      $input.off("apply.daterangepicker");
      $input.off("cancel.daterangepicker");
      $input.data("daterangepicker").remove();
    }
  };
};
